<template>
  <app-page-container custom="100%">
    <app-spinner v-if="!is_page_loaded" />
    <template v-else>
      <app-cells v-if="reservationData.reservation_status.code === 'booked_provisionally'">
        <app-form-field label="Deadline">
          <app-date-picker
            v-model="reservationData.reservation_deadline"
            placeholder="Deadline"
            :readonly="!is_general_manager && !is_safari_supervisor"
          />
        </app-form-field>
        <app-button
          v-if="is_general_manager || is_safari_supervisor"
          @click.native="onReservationPatch"
          ref="save_deadline"
          style="align-self: flex-end;"
        >
          Save
        </app-button>
      </app-cells>
      <app-page-container v-if="is_safari_supervisor">
        <app-form-field label="Reservation notes" label-for="reservation_notes">
          <app-textarea
            @change.native="onPatchReservationNotes"
            v-model="reservationData.reservation_notes"
            rows="5"
            placeholder="Write a note"
            id="reservation_notes"
          />
        </app-form-field>
      </app-page-container>
      <app-cells v-if="is_general_manager || is_safari_supervisor">
        <app-button @click.native="onClickArrangeCarsButton" ref="arrange_button">Arrange cars</app-button>
      </app-cells>
      <template v-if="table_own.items.length">
        <v-client-table
          :data="table_own.items"
          :columns="table_own.columns"
          :options="table_own.options"
          class="table-default"
        >
          <template #count="{ row, index }">{{ index }}</template>
          <template #company>Altezza</template>
          <template #driver="{ row }">
            <div
              v-if="row.driver"
              class="status-tag"
              :style="{ 'backgroundColor': row.driver_confirmed ? '#77b017' : '#f0c427' }"
            >
              {{ row.driver.full_name }}
            </div>
            <div v-else class="status-tag" style="background-color: #e85c20">Not assigned</div>
          </template>
          <template #plate_number="{ row }">
            <app-cells position="center" gap="10px" :indent="false">
              <div class="status-tag" :style="{ 'backgroundColor': row.car_confirmed ? '#77b017' : '#f0c427' }">
                {{ row.car.plate_number }}
              </div>
              <img v-if="row.primary_car" src="@/assets/img/tour-leader-icon.svg" alt="Leader">
            </app-cells>
          </template>
          <template #communication="{ row }">
            <div class="table-default__controls">
              <button
                v-if="row.driver"
                v-tooltip="{content: row.driver.phone, placement: 'top-start', offset: 10}"
                @click="onCopy(row.driver.phone)"
                class="table-default__activity table-default__activity--phone"
                :class="{'table-default__activity--active': row.driver.phone}"
              />
              <button v-else class="table-default__activity table-default__activity--phone" />
              <a
                v-if="row.driver"
                :href="`https://t.me/${row.driver.phone}`"
                target="_blank"
                class="table-default__activity table-default__activity--tg"
                :class="{'table-default__activity--active': row.driver.phone_telegram}"
              />
              <a v-else class="table-default__activity table-default__activity--tg" />
              <a
                v-if="row.driver"
                :href="`https://wa.me/${row.driver.phone}`"
                target="_blank"
                class="table-default__activity table-default__activity--wa"
                :class="{'table-default__activity--active': row.driver.phone_whatsapp}"
              />
              <a v-else class="table-default__activity table-default__activity--wa" />
            </div>
          </template>
        </v-client-table>
      </template>
      <template v-if="table_rental.items.length">
        <v-client-table
          :data="table_rental.items"
          :columns="table_rental.columns"
          :options="table_rental.options"
          class="table-default"
        >
          <template #count="{ row, index }">{{ index }}</template>
          <template #company="{ row }">{{ row.rental_company.name }}</template>
          <template #driver="{ row }">
            <div
              v-if="row.driver"
              class="status-tag"
              :style="{ 'backgroundColor': row.driver_confirmed ? '#77b017' : '#f0c427' }"
            >
              {{ row.driver.full_name }}
            </div>
            <div v-else class="status-tag" style="background-color: #e85c20">Not assigned</div>
          </template>
          <template #plate_number="{ row }">
            <app-cells position="center" gap="10px" :indent="false">
              <div class="status-tag" :style="{ 'backgroundColor': row.car_confirmed ? '#77b017' : '#f0c427' }">
                {{ row.car.plate_number }}
              </div>
              <img v-if="row.primary_car" src="@/assets/img/tour-leader-icon.svg" alt="Leader">
            </app-cells>
          </template>
          <template #communication="{ row }">
            <div class="table-default__controls">
              <button
                v-if="row.driver"
                v-tooltip="{content: row.driver.phone, placement: 'top-start', offset: 10}"
                @click="onCopy(row.driver.phone)"
                class="table-default__activity table-default__activity--phone"
                :class="{'table-default__activity--active': row.driver.phone}"
              />
              <button v-else class="table-default__activity table-default__activity--phone" />
              <a
                v-if="row.driver"
                :href="`https://t.me/${row.driver.phone}`"
                target="_blank"
                class="table-default__activity table-default__activity--tg"
                :class="{'table-default__activity--active': row.driver.phone_telegram}"
              />
              <a v-else class="table-default__activity table-default__activity--tg" />
              <a
                v-if="row.driver"
                :href="`https://wa.me/${row.driver.phone}`"
                target="_blank"
                class="table-default__activity table-default__activity--wa"
                :class="{'table-default__activity--active': row.driver.phone_whatsapp}"
              />
              <a v-else class="table-default__activity table-default__activity--wa" />
            </div>
          </template>
        </v-client-table>
      </template>
      <app-no-results
        v-if="!table_own.items.length && !table_rental.items.length"
        text="There’s no bookings yet"
      />
      <modal
        name="arrange-cars-popup"
        @before-close="beforeCarsClose"
        :width="700"
        :height="'auto'"
        :scrollable="true"
        :adaptive="true"
        :clickToClose="false"
        class="modal-not-overflow"
        draggable=".popup__title"
        :reset="true"
      >
        <div class="popup">
          <button @click="$modal.hide('arrange-cars-popup')" :disabled="carsSaving || assignmentLockPending" class="popup__close" type="button"></button>
          <div class="popup__title">Arrange cars</div>
          <div class="popup__body">
            <form @submit.prevent="onCheckArrangeForm">
              <p>Unlocking takes effect immediately. New locks are applied when you save.</p>
              <div class="arrange-popup">
                <div class="arrange-popup__part">
                  <div class="arrange-popup__block-title">
                    <span>Own cars</span>
                    <button @click="onAddOwnCar" type="button">
                      <img src="@/assets/img/add-big-button-icon.svg" alt="">
                    </button>
                  </div>
                  <div class="transfer-sortable transfer-sortable--own transfer-sortable--full">
                    <div v-for="(element, index) in own_cars" :key="`own_item_${index}`" class="transfer-sortable__item">
                      <div class="transfer-sortable__row">
                        <app-form-field required>
                          <app-select
                            @input="onOwnCarSelected(element.car, index)"
                            v-model="element.car"
                            :options="cars_options"
                            :clearable="false"
                            :get-option-label="item => item.plate_number"
                            :display-field="option => option.plate_number"
                            placeholder="Select car"
                            :readonly="element.car_assignment_locked || carsSaving || assignmentLockPending"
                            :error="$v.own_cars.$each[index].car.$error"
                          />
                          <template #error>
                            <div v-if="$v.own_cars.$each[index].car.$dirty && !$v.own_cars.$each[index].car.required">
                              Required field
                            </div>
                          </template>
                        </app-form-field>
                        <app-form-field>
                          <app-select
                            v-model="element.driver"
                            :options="drivers_options"
                            :clearable="true"
                            label="full_name"
                            placeholder="Enter driver"
                            :readonly="element.guide_assignment_locked || carsSaving || assignmentLockPending"
                          />
                        </app-form-field>
                      </div>
                      <div class="transfer-sortable__row">
                        <app-cells :indent="false">
                          Car confirmed: <app-switcher v-model="element.car_confirmed" :readonly="!element.car" />
                        </app-cells>
                        <app-cells :indent="false">
                          Driver confirmed: <app-switcher v-model="element.driver_confirmed" :readonly="!element.driver" />
                        </app-cells>
                      </div>
                      <div class="transfer-sortable__row">
                        <app-cells :indent="false">
                          Lock car:
                          <SafariAssignmentLock
                            :assignment="element" :safari-id="$route.params.id"
                            field="car_assignment_locked" :disabled="carsSaving || assignmentLockPending"
                            @busy="assignmentLockPending = $event"
                          />
                        </app-cells>
                        <app-cells :indent="false">
                          Lock guide:
                          <SafariAssignmentLock
                            :assignment="element" :safari-id="$route.params.id"
                            field="guide_assignment_locked" :disabled="carsSaving || assignmentLockPending"
                            @busy="assignmentLockPending = $event"
                          />
                        </app-cells>
                        <app-cells :indent="false">
                          Primary car:
                          <app-switcher
                            @change.native="onPrimarySwitcherChanged($event, 'own_cars', index)"
                            v-model="element.primary_car"
                            :readonly="!element.car"
                          />
                        </app-cells>
                      </div>
                      <button
                        @click="onRemoveOwnItem(index)"
                        class="transfer-sortable__close"
                        type="button"
                        :disabled="
                          carsSaving || assignmentLockPending ||
                          element.car_assignment_locked ||
                          element.guide_assignment_locked ||
                          element._initial_car_assignment_locked ||
                          element._initial_guide_assignment_locked
                        "
                      >
                        <img src="@/assets/img/delete-transfer.svg" alt="">
                      </button>
                    </div>
                  </div>
                </div>
                <div class="arrange-popup__part">
                  <div class="arrange-popup__block-title">
                    <span>Rental</span>
                    <button @click="onAddRental" type="button">
                      <img src="@/assets/img/add-big-button-icon.svg" alt="">
                    </button>
                  </div>
                  <div class="transfer-sortable transfer-sortable--own transfer-sortable--full">
                    <div v-for="(element, index) in rental_cars" :key="`rental_item_${index}`" class="transfer-sortable__item">
                      <div class="transfer-sortable__row">
                        <app-form-field>
                          <app-select
                            @input="onRentalCompanySelected(index)"
                            v-model="element.rental_company"
                            :options="company_options"
                            :clearable="false"
                            placeholder="Select company"
                            :error="$v.rental_cars.$each[index].rental_company.$error"
                          />
                          <template #error>
                            <div v-if="$v.rental_cars.$each[index].rental_company.$dirty && !$v.rental_cars.$each[index].rental_company.required">
                              Required field
                            </div>
                          </template>
                        </app-form-field>
                        <div class="arrange-popup__cost">
                          <span>Price (USD):</span>
                          <app-form-field>
                            <app-input
                              v-model.number="element.price"
                              type="number"
                              placeholder="0"
                              id="cost"
                              name="cost"
                              class="input--100"
                              :error="$v.rental_cars.$each[index].price.$error"
                            />
                            <template #error>
                              <div v-if="$v.rental_cars.$each[index].price.$dirty && !$v.rental_cars.$each[index].price.required">
                                Required field
                              </div>
                            </template>
                          </app-form-field>
                        </div>
                      </div>
                      <div class="transfer-sortable__row">
                        <app-form-field>
                          <app-select
                            @input="onRentalCarSelected(element.car, index)"
                            v-model="element.car"
                            :options="element.rental_company?.rental_cars"
                            :clearable="false"
                            label="plate_number"
                            placeholder="Select car"
                            :readonly="!element.rental_company"
                          />
                        </app-form-field>
                        <app-form-field>
                          <app-select
                            v-model="element.driver"
                            :options="element.rental_company?.drivers"
                            :clearable="true"
                            label="full_name"
                            placeholder="Select driver"
                            :readonly="!element.car"
                          />
                        </app-form-field>
                      </div>
                      <div class="transfer-sortable__row">
                        <app-cells :indent="false">
                          Car confirmed: <app-switcher v-model="element.car_confirmed" :readonly="!element.car" />
                        </app-cells>
                        <app-cells :indent="false">
                          Driver confirmed: <app-switcher v-model="element.driver_confirmed" :readonly="!element.driver" />
                        </app-cells>
                      </div>
                      <div class="transfer-sortable__row">
                        <app-cells :indent="false">
                          Primary car:
                          <app-switcher
                            @change.native="onPrimarySwitcherChanged($event, 'rental_cars', index)"
                            v-model="element.primary_car"
                            :readonly="!element.car"
                          />
                        </app-cells>
                      </div>
                      <button
                        @click="onRemoveRentalItem(index)"
                        class="transfer-sortable__close"
                        type="button"
                      >
                        <img src="@/assets/img/delete-transfer.svg" alt="">
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <app-cells position="center" :indent="false">
                <app-button :disabled="carsSaving || assignmentLockPending || $v.own_cars.$error || $v.rental_cars.$error" type="submit" ref="submit_arrange_cars">
                  Arrange cars
                </app-button>
              </app-cells>
            </form>
          </div>
        </div>
      </modal>
    </template>
  </app-page-container>
</template>

<script>
import { format, parseISO } from 'date-fns'
import { required } from 'vuelidate/lib/validators'
import {
  getCarManagementCars,
  getEmployeesListSearch,
  getReservationSafariCars,
  getSafariRentalCompanyCarsDrivers,
  patchReservationSafari,
  patchReservationSafariCars,
} from '@/http'
import {
  prepareSafariOwnCarAssignments,
} from '@/utils/safari-assignment-locks'
import SafariAssignmentLock from '@/components/SafariAssignmentLock'

export default {
  name: 'SafariReservationsDetailGeneral',
  components: { SafariAssignmentLock },
  props: {
    reservationData: Object,
  },
  data() {
    return {
      is_page_loaded: false,
      table_own: {
        items: [],
        columns: ['count', 'company', 'plate_number', 'driver', 'communication'],
        options: {
          headings: {
            count: '#',
            company: 'Company',
            plate_number: 'Car',
            driver: 'Driver',
            communication: 'Communication'
          },
          pagination: { show: false },
          sortable: [],
          perPage: 50,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      table_rental: {
        items: [],
        columns: ['count', 'company', 'plate_number', 'driver', 'communication'],
        options: {
          headings: {
            count: '#',
            company: 'Company',
            plate_number: 'Car',
            driver: 'Driver',
            communication: 'Communication',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 50,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      own_cars: [],
      carsSaving: false,
      assignmentLockPending: false,
      rental_cars: [],
      cars_options: [],
      drivers_options: [],
      company_options: [],
    }
  },
  validations: {
    own_cars: {
      $each: {
        car: { required },
      }
    },
    rental_cars: {
      $each: {
        rental_company: { required },
        price: { required },
      }
    }
  },
  computed: {
    is_safari_supervisor() {
      return this.$store.state.userGroups.map(item => item.code).includes('safari_supervisor')
    },
    is_general_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('general_manager')
    },
  },
  created() {
    this.fetchSafariCarsList()
  },
  methods: {
    onPatchReservationNotes() {
      patchReservationSafari(this.$route.params.id, { reservation_notes: this.reservationData.reservation_notes })
    },
    onReservationPatch() {
      this.$refs.save_deadline.preload = true
      patchReservationSafari(
        this.$route.params.id,
        { reservation_deadline: this.reservationData.reservation_deadline || null }
      )
        .finally(() => { this.$refs.save_deadline.preload = false })
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Data saved',
          })
          this.$emit('get-reservation-data')
        })
    },
    onPrimarySwitcherChanged(event, type, index) {
      if (event && (this.own_cars.some(item => item.primary_car) || this.rental_cars.some(item => item.primary_car))) {
        [...this.own_cars, ...this.rental_cars].forEach(el => {
          this.$set(el, 'primary_car', false)
        })
        this[type][index].primary_car = true
      }
    },
    fetchSafariCarsList() {
      getReservationSafariCars(this.$route.params.id).then(response => {
        this.own_cars = this.prepareOwnCars(response.data.own_cars)
        this.rental_cars = structuredClone(response.data.rental_cars)
        this.table_own.items = structuredClone(response.data.own_cars)
        this.table_rental.items = structuredClone(response.data.rental_cars)
        this.is_page_loaded = true
      })
    },
    onCheckArrangeForm() {
      this.$v.own_cars.$touch()
      this.$v.rental_cars.$touch()
      if (this.$v.own_cars.$invalid || this.$v.rental_cars.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else this.sendForm()
    },
    sendForm() {
      if (this.carsSaving || this.assignmentLockPending) return
      this.carsSaving = true
      this.$refs.submit_arrange_cars.preload = true
      return patchReservationSafariCars(this.$route.params.id, {
        own_cars: this.normalizeOwnCarsForm(),
        rental_cars: this.normalizeRentalCarsForm(),
      })
        .finally(() => { this.carsSaving = false; this.$refs.submit_arrange_cars.preload = false })
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Cars arranged',
          })
          this.fetchSafariCarsList()
          this.$modal.hide('arrange-cars-popup')
        })
        .catch(() => {})
    },
    beforeCarsClose(event) {
      if (this.carsSaving || this.assignmentLockPending) event.cancel()
    },
    normalizeOwnCarsForm() {
      let own_cars = [ ...this.own_cars ]

      if (own_cars.length) {
        own_cars = own_cars.map(item => {
          const normalized = {
            ...item,
            car: item.car.id,
            driver: item.driver ? item.driver.id : null,
          }
          delete normalized._initial_car_assignment_locked
          delete normalized._initial_guide_assignment_locked
          return normalized
        })
      } else own_cars = []

      return own_cars
    },
    normalizeRentalCarsForm() {
      let rental_cars = [ ...this.rental_cars ]

      if (rental_cars.length) {
        rental_cars = rental_cars.map(item => {
          return {
            ...item,
            rental_company: item.rental_company.id,
            driver: item.driver ? item.driver.id : null,
            car: item.car.id,
          }
        })
      } else rental_cars = []

      return rental_cars
    },
    onRemoveRentalItem(index) {
      this.rental_cars.splice(index, 1)
    },
    onRentalCarSelected(car, index) {
      this.rental_cars[index].driver = null
      if (car.driver && car.driver.length && car.driver.length === 1) {
        this.rental_cars[index].driver = car.driver[0]
      }
    },
    onRentalCompanySelected(index) {
      this.rental_cars[index].car = null
      this.rental_cars[index].driver = null
    },
    onAddRental() {
      this.rental_cars.push({
        rental_company: null,
        car: null,
        driver: null
      })
    },
    onRemoveOwnItem(index) {
      if (this.carsSaving || this.assignmentLockPending) return
      const assignment = this.own_cars[index]
      if (
        assignment.car_assignment_locked ||
        assignment.guide_assignment_locked ||
        assignment._initial_car_assignment_locked ||
        assignment._initial_guide_assignment_locked
      ) return
      this.own_cars.splice(index, 1)
    },
    onOwnCarSelected(car, index) {
      if (this.own_cars[index].guide_assignment_locked) return
      this.own_cars[index].driver = null
      if (car.driver) this.own_cars[index].driver = car.driver
      else this.own_cars[index].driver = null
    },
    onAddOwnCar() {
      this.own_cars.push({
        car: null,
        driver: null,
        car_assignment_locked: false,
        guide_assignment_locked: false,
        _initial_car_assignment_locked: false,
        _initial_guide_assignment_locked: false,
      })
    },
    prepareOwnCars(assignments) {
      return prepareSafariOwnCarAssignments(structuredClone(assignments))
    },
    onClickArrangeCarsButton() {
      this.$refs.arrange_button.preload = true
      this.$v.own_cars.$reset()
      this.$v.rental_cars.$reset()
      Promise.all([
        getCarManagementCars({ safari: true }),
        getEmployeesListSearch({ position: 'safari_driver',  employment_status: 'working' }),
        getSafariRentalCompanyCarsDrivers(),
        getReservationSafariCars(this.$route.params.id)
      ])
        .then(res => {
          this.cars_options = res[0].data
          this.drivers_options = res[1].data
          this.company_options = res[2].data
          this.own_cars = this.prepareOwnCars(res[3].data.own_cars)
          this.rental_cars = structuredClone(res[3].data.rental_cars)
          this.$modal.show('arrange-cars-popup')
        })
        .finally(() => { this.$refs.arrange_button.preload = false })
        .catch(() => {})
    },
    onCopy(link) {
      navigator.clipboard.writeText(link)
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Copied to clipboard',
          })
        })
        .catch(() => { console.error('Something went wrong') })
    },
  }
}
</script>

<style scoped>
.assignment-lock-switcher.switcher--active ::v-deep .switcher__body {
  background-color: #e5ad18;
}
</style>
